import { MessageTemplateResponse } from "@accurx/api/patient-messaging";
import { Log } from "@accurx/shared";

import { MessageTemplateOwner, MessageTemplateType } from "api/FlemingDtos";
import { ProductSelection } from "app/selectProduct/ProductSelection";
import {
    PreviewUrl,
    TemplateInList,
} from "app/workspaceConversations/components/MessageTemplates/MessageTemplates.types";
import { MessageTemplateOption, PresetTemplate } from "shared/Templates";

/**
 * Helper function that transform a preset template
 * into a MessageTemplate object
 *
 * The current selected template - we need to ensure that ALL templates take the same shape
 * of MessageTemplate object, so we can very easily send analytics events consistently
 * whether the selectedTemplate is a custom one (MessageTemplate type) or
 * it's a preset one (PresetTemplate)
 */
export const mapPresetTemplateToMessageTemplate = (
    presetTemplate: PresetTemplate,
    productSelection: ProductSelection,
    group: string,
): MessageTemplateOption => {
    // for preset templates the type is whatever product is currently selected
    // but nothing else should be set
    const isVideoConsult = productSelection === ProductSelection.Video;
    const templateType = isVideoConsult
        ? MessageTemplateType.VideoConsult
        : MessageTemplateType.Sms;

    return {
        id: null,
        body: presetTemplate.body,
        title: presetTemplate.title,
        isPatientResponseRequired: presetTemplate.isPatientResponseRequired,
        type: templateType,
        isDefault: null,
        owner: null,
        isPresetTemplate: true,
        group: group,
    };
};

export const createPreviewUrl = ({
    orgId,
    templateId,
    documentId,
}: {
    orgId: number | null;
    templateId: number | null;
    documentId: string;
}): PreviewUrl => {
    if (orgId === null || templateId === null) {
        Log.error(
            "Trying to create a template attachment preview URL, but not all the data provided",
            {
                tags: {
                    orgId,
                    templateId,
                },
            },
        );
    }

    return `/api/patientMessaging/template/message/${orgId}/${templateId}/attachment/${documentId}`;
};
const mapTemplateStringTypeToEnum = (
    type: Optional<string>,
): MessageTemplateType => {
    switch (type) {
        case "PatientAdvice":
            return MessageTemplateType.Sms;
        case "VideoInvite":
            return MessageTemplateType.VideoConsult;
        default:
            throw new Error(`Unspported template type: ${type}`);
    }
};

const mapTemplateStringOwnerToEnum = (
    owner: Optional<string>,
): MessageTemplateOwner => {
    switch (owner) {
        case "User":
            return MessageTemplateOwner.User;
        case "Organisation":
            return MessageTemplateOwner.Organisation;
        default:
            throw new Error(`Unspported template owner: ${owner}`);
    }
};

export const mapMessageTemplateResponseToTemplateInList = (
    messageTemplateResponse: MessageTemplateResponse,
    orgId: number | null,
): TemplateInList => ({
    id: messageTemplateResponse.id
        ? parseInt(messageTemplateResponse.id, 10)
        : null,
    title: messageTemplateResponse.title,
    body: messageTemplateResponse.body,
    type: mapTemplateStringTypeToEnum(messageTemplateResponse.type),
    isDefault: messageTemplateResponse.isDefault,
    owner: mapTemplateStringOwnerToEnum(messageTemplateResponse.owner),
    attachedDocuments: messageTemplateResponse.attachments?.map(
        (attachment) => ({
            documentId: attachment.id.toString(),
            fileSize: attachment.fileSize,
            // this shouldn't ever be null, but our generated API types tell us that it can,
            // so we have to handle it by using a non-null assertion
            // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
            fileName: attachment.fileName!,
            previewUrl: createPreviewUrl({
                orgId,
                documentId: attachment.id.toString(),
                templateId: messageTemplateResponse.id
                    ? parseInt(messageTemplateResponse.id, 10)
                    : null,
            }),
        }),
    ),
});
